import { checkCharacterIntegrity } from '../app/characterIntegrity.ts';
import type { Character } from '../domain/character.ts';
import { CHANGE_LOG_LIMIT, findInventoryEntry, findSkillRank } from '../domain/character.ts';
import type {
  AppliedChange,
  CharacterChange,
  CharacterChangeRequest,
} from '../domain/characterChange.ts';
import { describeChange } from '../domain/characterChange.ts';
import { validateCharacter } from '../domain/validateCharacter.ts';
import {
  hotlistDuplicateProblem,
  hotlistSlotIndexProblem,
  isHotlistEntry,
} from './hotlist.ts';
import type { InventoryCatalogue } from './inventory.ts';
import type { Item, Skill } from '../content/types.ts';
import { isLegalSize } from './size.ts';
import { isSkillLegalFor } from './skillAvailability.ts';
import { MAX_RANK_FLOORS_1_TO_5 } from './skillAdvancement.ts';

/**
 * THE one place a Character is modified.
 *
 * Creation, advancement, the Tutorial award, play and manual editing all pass
 * `CharacterChange` records through here. That means legality is checked once,
 * the audit trail is written once, and a fifth caller cannot quietly invent a
 * different set of rules.
 *
 * Pure: it takes a Character and returns a new one. Nothing is saved here.
 *
 * RULE DCC-EDIT-001 - a batch is ATOMIC. If any change in it is illegal, none
 * of them are applied. Half-editing a crawler is worse than refusing.
 *
 * RULE DCC-EDIT-002 - when the caller supplies a Skill catalogue, a Skill the
 * catalogue does not know, or one the crawler's type may not take, is refused.
 * Source for the restriction itself: Crawlers & Customization p.10 (animal-only
 * Skills). This is checked HERE rather than in an editing screen so that every
 * caller gets the same answer.
 */

export interface ChangeProblem {
  /** Index of the offending change within the batch. */
  index: number;
  message: string;
}

export type ApplyChangesResult =
  | { ok: true; character: Character; applied: AppliedChange[] }
  | { ok: false; character: Character; problems: ChangeProblem[] };

export interface ApplyOptions {
  now: string;
  /**
   * Ranks above this are refused. Defaults to the Floors 1-5 cap of 15
   * (C&C p.42); a Race or Class that lifts a specific Skill to 20 will pass a
   * higher value when that slice exists.
   */
  maxSkillRank?: number;
  /**
   * Optional Skill catalogue lookup, for RULE DCC-EDIT-002.
   *
   * Supplied by callers that accept Skill IDs chosen by a person (the Edit
   * Character screen). Omitted by callers whose Skill IDs already came from the
   * catalogue (creation, advancement, the Tutorial award) and by the importer,
   * which deliberately preserves unknown IDs from other content packages
   * rather than deleting them. When it is omitted nothing extra is checked, so
   * existing behaviour is unchanged.
   */
  lookupSkill?: (skillId: string) => Skill | undefined;
  /**
   * Optional catalogue, the same reason as `lookupSkill`: an ID that came from
   * a person is checked, and one that came from the catalogue already needs no
   * checking.
   *
   * Items and Attacks travel TOGETHER as one `InventoryCatalogue`. Held
   * occupancy depends on both (RULE DCC-INV-016), so a caller holding only the
   * Item half would be answering the wrong question. What stays optional is
   * whether a catalogue is available at all, never which half of one.
   */
  catalogue?: InventoryCatalogue;
}

export function applyChanges(
  character: Character,
  requests: readonly CharacterChangeRequest[],
  options: ApplyOptions,
): ApplyChangesResult {
  const maxRank = options.maxSkillRank ?? MAX_RANK_FLOORS_1_TO_5;
  const problems: ChangeProblem[] = [];
  const applied: AppliedChange[] = [];

  let working = character;

  requests.forEach((request, index) => {
    const problem = checkChange(
      working,
      request.change,
      maxRank,
      options.lookupSkill,
      options.catalogue?.lookupItem,
    );
    if (problem) {
      problems.push({ index, message: problem });
      return;
    }
    const undo = inverseOf(working, request.change);
    working = applyOne(working, request.change);
    applied.push({ ...request, at: options.now, undo });
  });

  if (problems.length > 0) return { ok: false, character, problems };

  // The result must still be a valid crawler, whatever the individual changes
  // said. This catches combinations no single check could see.
  const withLog: Character = {
    ...working,
    changeLog: [...working.changeLog, ...applied].slice(-CHANGE_LOG_LIMIT),
  };
  // RULE DCC-INV-003/004 - Gear Slot legality is an INVARIANT, not a UI rule,
  // because equipped items feed Effects: two Torso items worn at once would
  // contribute both bonuses. Checked here whenever a catalogue is available, so
  // a `set-item` change cannot reach the disk with an impossible layout. UI
  // controls are not an integrity boundary. `validateCharacter` itself stays
  // catalogue-independent, so it never reaches for a global registry.
  if (options.catalogue) {
    const integrity = checkCharacterIntegrity(withLog, options.catalogue);
    if (!integrity.ok) return { ok: false, character, problems: integrity.problems.map((message) => ({ index: -1, message })) };
  }

  const report = validateCharacter(withLog);
  if (!report.valid) {
    return {
      ok: false,
      character,
      problems: report.errors.map((message) => ({ index: -1, message })),
    };
  }

  return { ok: true, character: withLog, applied };
}

/**
 * Reverses a batch, newest first. The crawler returns to its earlier state.
 *
 * Each entry's `undo` is a list applied IN ORDER: `remove-skill` reverses to
 * `add-skill` then `add-advancement-mark`, and the mark cannot be restored
 * before the Skill it belongs to exists. Entries with an empty `undo` were
 * no-ops and contribute nothing.
 */
export function undoChanges(
  character: Character,
  applied: readonly AppliedChange[],
  options: ApplyOptions,
): ApplyChangesResult {
  const requests: CharacterChangeRequest[] = [...applied]
    .reverse()
    .flatMap((entry) =>
      entry.undo.map(
        (change): CharacterChangeRequest => ({
          change,
          source: entry.source,
          reason: `Undo: ${describeChange(entry.change)}`,
        }),
      ),
    );
  if (requests.length === 0) {
    // Everything being reversed was a no-op. Nothing to do, and nothing wrong.
    return { ok: true, character, applied: [] };
  }
  return applyChanges(character, requests, options);
}

/** Null when the change is legal, otherwise the reason it is not. */
function checkChange(
  character: Character,
  change: CharacterChange,
  maxRank: number,
  lookupSkill?: (skillId: string) => Skill | undefined,
  lookupItem?: (itemId: string) => Item | undefined,
): string | null {
  switch (change.kind) {
    case 'set-name':
      return change.name.trim().length === 0 ? 'A crawler needs a name.' : null;

    case 'set-size':
      return isLegalSize(character.crawlerType, change.size)
        ? null
        : `Size ${change.size} is not legal for a ${character.crawlerType} crawler (C&C p.16, Table 10).`;

    case 'set-crawler-type': {
      // The size that was legal before may not be legal now.
      if (!isLegalSize(change.crawlerType, character.size)) {
        return `Size ${character.size} is not legal for a ${change.crawlerType} crawler; set the size in the same batch.`;
      }
      // RULE DCC-EDIT-002 - nor may the Skills the crawler already holds.
      // Ordering matters and that is deliberate: drop the offending Skills
      // earlier in the same batch and the whole thing succeeds atomically.
      if (lookupSkill) {
        const stranded = character.skills
          .map((s) => lookupSkill(s.skillId))
          .filter((s): s is Skill => s !== undefined)
          .filter((s) => !isSkillLegalFor(s, change.crawlerType))
          .map((s) => s.name);
        if (stranded.length > 0) {
          return `A ${change.crawlerType} crawler may not have ${stranded.join(', ')} (C&C p.10); remove them in the same batch.`;
        }
      }
      return null;
    }

    case 'set-stat':
      return wholeAtLeast(change.value, 1) ? null : 'A Stat must be a whole number of at least 1.';

    case 'set-stats':
      return Object.values(change.stats).every((v) => wholeAtLeast(v, 1))
        ? null
        : 'Every Stat must be a whole number of at least 1.';

    case 'add-skill': {
      if (findSkillRank(character, change.skillId) > 0) {
        return `The crawler already has ${change.skillId}. Use set-skill-rank instead.`;
      }
      // RULE DCC-EDIT-002 - only when a catalogue was supplied.
      if (lookupSkill) {
        const skill = lookupSkill(change.skillId);
        if (!skill) return `There is no Skill with the ID "${change.skillId}".`;
        if (!isSkillLegalFor(skill, character.crawlerType)) {
          return `${skill.name} is only available to animal crawlers (C&C p.10).`;
        }
      }
      return rankProblem(change.rank, maxRank, lookupSkill?.(change.skillId));
    }

    case 'remove-skill':
      return character.skills.some((s) => s.skillId === change.skillId)
        ? null
        : `The crawler does not have ${change.skillId}.`;

    case 'set-skill-rank':
      if (!character.skills.some((s) => s.skillId === change.skillId)) {
        return `The crawler does not have ${change.skillId}. Use add-skill instead.`;
      }
      return rankProblem(change.rank, maxRank, lookupSkill?.(change.skillId));

    case 'adjust-skill-rank': {
      if (!character.skills.some((s) => s.skillId === change.skillId)) {
        return `The crawler does not have ${change.skillId}.`;
      }
      return rankProblem(
        findSkillRank(character, change.skillId) + change.delta,
        maxRank,
        lookupSkill?.(change.skillId),
      );
    }

    case 'set-stats-and-advancement': {
      if (!Object.values(change.stats).every((v) => wholeAtLeast(v, 1))) return 'Every Stat must be a whole number of at least 1.';
      const p = change.advancement;
      return wholeAtLeast(p.levelAccountedFor, 1) && p.levelAccountedFor <= 250 && wholeAtLeast(p.unspentPoints, 0) && p.unspentPoints <= 747 && typeof p.needsReview === 'boolean' ? null : 'Invalid Stat Point balance.';
    }
    case 'set-stat-advancement': {
      const p = change.advancement;
      return wholeAtLeast(p.levelAccountedFor, 1) && p.levelAccountedFor <= 250 && wholeAtLeast(p.unspentPoints, 0) && p.unspentPoints <= 747 && typeof p.needsReview === 'boolean' ? null : 'Invalid Stat Point balance.';
    }
    case 'set-level':
      return wholeAtLeast(change.level, 1) && change.level <= 250 ? null : 'Level must be a whole number from 1 to 250.';

    case 'adjust-level':
      return wholeAtLeast(character.level + change.delta, 1) && character.level + change.delta <= 250
        ? null
        : 'Level must stay between 1 and 250.';

    case 'set-floor':
      return wholeAtLeast(change.floor, 1) ? null : 'Floor must be a whole number of at least 1.';

    case 'add-advancement-mark':
      return character.skills.some((s) => s.skillId === change.skillId)
        ? null
        : `Cannot mark ${change.skillId}: the crawler does not have it.`;

    case 'set-health-bar-slots':
      return wholeAtLeast(change.slotsRemaining, 0)
        ? null
        : 'Health Bar slots must be a whole number of at least 0.';

    case 'set-current-mana':
      return wholeAtLeast(change.currentMana, 0)
        ? null
        : 'Mana must be a whole number of at least 0.';

    case 'set-ai-favor':
      return wholeAtLeast(change.aiFavor, 0)
        ? null
        : 'AI Favor must be a whole number of at least 0.';

    case 'set-dying-rounds':
      return change.dyingRoundsRemaining === null || wholeAtLeast(change.dyingRoundsRemaining, 0)
        ? null
        : 'Dying rounds must be a whole number or null.';

    case 'clear-advancement-marks':
    case 'set-notes':
      return null;

    case 'add-item': {
      if (!Number.isInteger(change.quantity) || change.quantity < 1) {
        return 'An item quantity must be a whole number of at least 1.';
      }
      if (lookupItem) {
        if (!lookupItem(change.itemId)) return `There is no Item with the ID "${change.itemId}".`;
      }
      return null;
    }
    case 'set-item': {
      if (!Number.isInteger(change.quantity) || change.quantity < 1) {
        return 'An item quantity must be a whole number of at least 1. Use remove-item to take it away.';
      }
      if (!Number.isInteger(change.equipped) || change.equipped < 0) {
        return 'An equipped count must be a whole number of at least 0.';
      }
      if (!Number.isInteger(change.held) || change.held < 0) {
        return 'A held count must be a whole number of at least 0.';
      }
      // RULE DCC-INV-018 - the two allocations together cannot exceed what is
      // owned. A copy is one physical object and can only be in one place.
      if (change.equipped + change.held > change.quantity) {
        return (
          `Cannot have ${change.equipped} equipped and ${change.held} held when only ` +
          `${change.quantity} are owned.`
        );
      }
      // RULE DCC-INV-002. Wearing more than you own would grant bonuses for
      // items that do not exist.
      if (change.equipped > change.quantity) {
        return `Cannot have ${change.equipped} equipped when only ${change.quantity} are owned.`;
      }
      // RULE DCC-INV-012 - `set-item` is the PRESERVATION pathway, not the
      // acquisition one, and the two need different answers about an Item this
      // installation does not know.
      //
      // `add-item` above still requires a known Item: acquiring something we
      // have no definition for is a mistake worth refusing. But an unknown
      // unequipped Item the crawler already owns is a state this project keeps
      // deliberately - deleting a player's possession because we lack its
      // definition is worse than not knowing what it is - and `remove-item`
      // inverts to `set-item`. Refusing every unknown Item here made that
      // inverse illegal, so removing an unresolved Item could not be undone
      // and the exact-reversal guarantee of CharacterChange was a lie.
      //
      // EQUIPPED is where the line sits, because equipped is the only part of
      // an entry that can grant anything. An unknown Item with copies in a
      // Gear Slot stays refused: we cannot know its slot, so we cannot know it
      // fits, and `validateInventoryAgainstCatalogue` - which remains the
      // authoritative catalogue-aware check - refuses that same state below.
      if (lookupItem && !lookupItem(change.itemId) && change.equipped + change.held > 0) {
        return `There is no Item with the ID "${change.itemId}", so none can be equipped.`;
      }
      return null;
    }
    case 'remove-item':
      return findInventoryEntry(character, change.itemId)
        ? null
        : `The crawler has no ${change.itemId} to remove.`;
    case 'set-origin':
      return null;

    /**
     * RULE DCC-HOT-001. This is the PRESERVATION pathway, and it is
     * deliberately looser than the ordinary Add - exactly the split `add-item`
     * and `set-item` make for Inventory under RULE DCC-INV-012.
     *
     * What is checked here is what must hold for the record to make sense: the
     * slot exists, and no two slots name the same thing.
     *
     * What is NOT checked here, and why: whether the reference resolves, and
     * whether the crawler still owns it. Both are required by
     * `hotlistAddProblem` when a player ADDS something. Requiring them here as
     * well would make an Undo illegal - restoring a shortcut to the last potion
     * the crawler just drank, or to a custom Spell this machine lacks - and the
     * exact-reversal guarantee of `CharacterChange` would be a lie. It costs
     * nothing to be lenient, because RULE DCC-HOT-018 gives a Hotlist entry no
     * mechanical effect at all: an entry naming missing content is inert, which
     * is not true of an equipped Item.
     */
    case 'set-hotlist-slot': {
      const slotProblem = hotlistSlotIndexProblem(change.slot);
      if (slotProblem) return slotProblem;
      if (change.entry === null) return null;
      if (!isHotlistEntry(change.entry)) return 'That is not a valid Hotlist entry.';
      return hotlistDuplicateProblem(character.hotlist, change.slot, change.entry);
    }
    case 'swap-hotlist-slots': {
      return hotlistSlotIndexProblem(change.from) ?? hotlistSlotIndexProblem(change.to);
    }
  }
}

function applyOne(character: Character, change: CharacterChange): Character {
  switch (change.kind) {
    case 'set-name':
      return { ...character, name: change.name.trim() };
    case 'set-crawler-type':
      return { ...character, crawlerType: change.crawlerType };
    case 'set-size':
      return { ...character, size: change.size };
    case 'set-stats':
      return { ...character, unenhancedStats: { ...change.stats } };
    case 'set-stat':
      return {
        ...character,
        unenhancedStats: { ...character.unenhancedStats, [change.stat]: change.value },
      };
    case 'add-skill':
      return {
        ...character,
        skills: [...character.skills, { skillId: change.skillId, rank: change.rank }],
      };
    case 'remove-skill':
      return {
        ...character,
        skills: character.skills.filter((s) => s.skillId !== change.skillId),
        // A Skill the crawler no longer has cannot stay marked.
        advancementMarks: character.advancementMarks.filter((id) => id !== change.skillId),
      };
    case 'set-skill-rank':
      return {
        ...character,
        skills: character.skills.map((s) =>
          s.skillId === change.skillId ? { ...s, rank: change.rank } : s,
        ),
      };
    case 'adjust-skill-rank':
      return {
        ...character,
        skills: character.skills.map((s) =>
          s.skillId === change.skillId ? { ...s, rank: s.rank + change.delta } : s,
        ),
      };
    case 'set-stats-and-advancement':
      return { ...character, unenhancedStats: { ...change.stats }, statAdvancement: { ...change.advancement } };
    case 'set-stat-advancement':
      return { ...character, statAdvancement: { ...change.advancement } };
    case 'set-level':
      return { ...character, level: change.level };
    case 'adjust-level':
      return { ...character, level: character.level + change.delta };
    case 'set-floor':
      return { ...character, floor: change.floor };
    case 'add-advancement-mark':
      return character.advancementMarks.includes(change.skillId)
        ? character
        : { ...character, advancementMarks: [...character.advancementMarks, change.skillId] };
    case 'clear-advancement-marks': {
      const cleared = new Set(change.skillIds);
      return {
        ...character,
        advancementMarks: character.advancementMarks.filter((id) => !cleared.has(id)),
      };
    }
    case 'set-health-bar-slots':
      return { ...character, healthBarSlotsRemaining: change.slotsRemaining };
    case 'set-current-mana':
      return { ...character, currentMana: change.currentMana };
    case 'set-ai-favor':
      return { ...character, aiFavor: change.aiFavor };
    case 'set-dying-rounds':
      return { ...character, dyingRoundsRemaining: change.dyingRoundsRemaining };
    case 'set-notes':
      return { ...character, notes: change.notes };
    case 'add-item': {
      const existing = findInventoryEntry(character, change.itemId);
      const inventory = existing
        ? character.inventory.map((e) =>
            e.itemId === change.itemId ? { ...e, quantity: e.quantity + change.quantity } : e,
          )
        : [
            ...character.inventory,
            // RULE DCC-INV-018 - acquiring something never puts it in a hand.
            { itemId: change.itemId, quantity: change.quantity, equipped: 0, held: 0 },
          ];
      return { ...character, inventory };
    }
    case 'set-item': {
      const existing = findInventoryEntry(character, change.itemId);
      const entry = {
        itemId: change.itemId,
        quantity: change.quantity,
        equipped: change.equipped,
        held: change.held,
      };
      return {
        ...character,
        inventory: existing
          ? character.inventory.map((e) => (e.itemId === change.itemId ? entry : e))
          : [...character.inventory, entry],
      };
    }
    case 'remove-item':
      return {
        ...character,
        inventory: character.inventory.filter((e) => e.itemId !== change.itemId),
      };
    case 'set-origin':
      return { ...character, origin: change.origin };
    case 'set-hotlist-slot': {
      const hotlist = character.hotlist.map((slot, index) =>
        index === change.slot ? change.entry : slot,
      );
      return { ...character, hotlist };
    }
    case 'swap-hotlist-slots': {
      const hotlist = [...character.hotlist];
      const from = hotlist[change.from] ?? null;
      const to = hotlist[change.to] ?? null;
      hotlist[change.from] = to;
      hotlist[change.to] = from;
      return { ...character, hotlist };
    }
  }
}

/**
 * The changes that return the crawler to where it was, applied in order.
 *
 * Read against `applyOne`, not against the change's name. Any change whose
 * `applyOne` touches a SECOND field needs that field restored here too, or the
 * reversal guarantee is a lie. Two currently do:
 *
 *  - `remove-skill` also drops the Skill's advancement mark;
 *  - `clear-advancement-marks` drops several marks at once.
 *
 * And any change that can be a NO-OP must invert to an empty list, or undoing
 * it would destroy state the change never created. `add-advancement-mark` on an
 * already-marked Skill is the case that matters.
 */
function inverseOf(character: Character, change: CharacterChange): CharacterChange[] {
  switch (change.kind) {
    case 'set-name':
      return [{ kind: 'set-name', name: character.name }];
    case 'set-crawler-type':
      return [{ kind: 'set-crawler-type', crawlerType: character.crawlerType }];
    case 'set-size':
      return [{ kind: 'set-size', size: character.size }];
    case 'set-stats':
      return [{ kind: 'set-stats', stats: { ...character.unenhancedStats } }];
    case 'set-stat':
      return [
        {
          kind: 'set-stat',
          stat: change.stat,
          value: character.unenhancedStats[change.stat],
        },
      ];
    case 'add-skill':
      // A mark cannot exist for a Skill the crawler does not have: every
      // pathway that adds one requires the Skill, and `remove-skill` drops it.
      // So there is no mark state to preserve here.
      return [{ kind: 'remove-skill', skillId: change.skillId }];
    case 'remove-skill': {
      // `applyOne` drops the Skill AND its mark. Both come back, Skill first:
      // `add-advancement-mark` is refused for a Skill the crawler lacks.
      const restore: CharacterChange[] = [
        {
          kind: 'add-skill',
          skillId: change.skillId,
          rank: findSkillRank(character, change.skillId),
        },
      ];
      if (character.advancementMarks.includes(change.skillId)) {
        restore.push({ kind: 'add-advancement-mark', skillId: change.skillId });
      }
      return restore;
    }
    case 'set-skill-rank':
    case 'adjust-skill-rank':
      return [
        {
          kind: 'set-skill-rank',
          skillId: change.skillId,
          rank: findSkillRank(character, change.skillId),
        },
      ];
    case 'set-stats-and-advancement':
      return [{ kind: 'set-stats-and-advancement', stats: { ...character.unenhancedStats }, advancement: character.statAdvancement ?? { levelAccountedFor: character.level, unspentPoints: 0, needsReview: true } }];
    case 'set-stat-advancement':
      return [{ kind: 'set-stat-advancement', advancement: character.statAdvancement ?? { levelAccountedFor: character.level, unspentPoints: 0, needsReview: true } }];
    case 'set-level':
    case 'adjust-level':
      return [{ kind: 'set-level', level: character.level }];
    case 'set-floor':
      return [{ kind: 'set-floor', floor: character.floor }];
    case 'add-advancement-mark':
      // Marking an already-marked Skill changes nothing, so reversing it must
      // change nothing. Clearing the mark here would destroy a mark that
      // existed before the request was ever made.
      return character.advancementMarks.includes(change.skillId)
        ? []
        : [{ kind: 'clear-advancement-marks', skillIds: [change.skillId] }];
    case 'clear-advancement-marks':
      // Restore exactly the marks that were actually there to be cleared.
      // Marks named in the request but not held were never removed.
      return change.skillIds
        .filter((id) => character.advancementMarks.includes(id))
        .map((id): CharacterChange => ({ kind: 'add-advancement-mark', skillId: id }));
    case 'set-health-bar-slots':
      return [{ kind: 'set-health-bar-slots', slotsRemaining: character.healthBarSlotsRemaining }];
    case 'set-current-mana':
      return [{ kind: 'set-current-mana', currentMana: character.currentMana }];
    case 'set-ai-favor':
      return [{ kind: 'set-ai-favor', aiFavor: character.aiFavor }];
    case 'set-dying-rounds':
      return [{ kind: 'set-dying-rounds', dyingRoundsRemaining: character.dyingRoundsRemaining }];
    case 'set-notes':
      return [{ kind: 'set-notes', notes: character.notes }];
    case 'add-item': {
      // Restores the ENTRY as it was, not just the count. Adding to an item
      // the crawler already had must not reset how many were equipped, and
      // adding a brand new one must take the whole entry away again.
      const before = findInventoryEntry(character, change.itemId);
      return before
        ? [
            {
              kind: 'set-item',
              itemId: before.itemId,
              quantity: before.quantity,
              equipped: before.equipped,
              // RULE DCC-INV-018 - the hand comes back too.
              held: before.held,
            },
          ]
        : [{ kind: 'remove-item', itemId: change.itemId }];
    }
    case 'set-item': {
      const before = findInventoryEntry(character, change.itemId);
      return before
        ? [
            {
              kind: 'set-item',
              itemId: before.itemId,
              quantity: before.quantity,
              equipped: before.equipped,
              // RULE DCC-INV-018 - the hand comes back too.
              held: before.held,
            },
          ]
        : [{ kind: 'remove-item', itemId: change.itemId }];
    }
    case 'remove-item': {
      // The lesson from `remove-skill`: an inverse must restore EVERY field the
      // change touched. Removing an item drops the quantity AND the equipped
      // count, so both come back. `add-item` alone would return the item
      // unworn, silently taking away the armour's DR.
      const before = findInventoryEntry(character, change.itemId);
      if (!before) return [];
      return [
        {
          kind: 'set-item',
          itemId: before.itemId,
          quantity: before.quantity,
          equipped: before.equipped,
          held: before.held,
        },
      ];
    }
    case 'set-origin':
      return [{ kind: 'set-origin', origin: character.origin }];
    case 'set-hotlist-slot':
      // A slot holds one thing, so restoring what was there restores
      // everything the change touched. `null` is a real previous value.
      return [
        {
          kind: 'set-hotlist-slot',
          slot: change.slot,
          entry: character.hotlist[change.slot] ?? null,
        },
      ];
    case 'swap-hotlist-slots':
      // Its own inverse. Swapping back is exact whichever slots were empty.
      return [{ kind: 'swap-hotlist-slots', from: change.from, to: change.to }];
  }
}

function rankProblem(rank: number, maxRank: number, skill?: Skill): string | null {
  if (!Number.isInteger(rank)) return 'A Skill Rank must be a whole number.';
  if (rank < 1) return 'A Skill at Rank 0 is not recorded (C&C p.6). Remove it instead.';
  // RULE DCC-SPELL-004 - a Skill's OWN printed ceiling constrains the scope cap
  // rather than being checked somewhere later. Heal is "Rank 1 maximum" (C&C
  // p.38), so a Rank 2 Heal is a sheet the rules do not allow and must not be
  // creatable in the first place. Only when a catalogue was supplied: the
  // change layer stays usable without one (RULE DCC-EDIT-002).
  if (skill?.maxRank !== undefined && rank > skill.maxRank) {
    return `${skill.name} is limited to Rank ${skill.maxRank} (C&C p.38).`;
  }
  if (rank > maxRank) return `Skill Rank is capped at ${maxRank} in the current scope (C&C p.42).`;
  return null;
}

function wholeAtLeast(value: number, minimum: number): boolean {
  return Number.isInteger(value) && value >= minimum;
}
